import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Center, Field, Icon, Sheet, Spinner } from '@barev/ui';
import { errorText, haptic, useI18n, useToast, useTRPC } from '@barev/web';
import { useState } from 'react';
import { LEVEL_NAME, PageHead, type Level } from '../lib.tsx';

/** Экран «Барики»: баланс, ожидание, сгорание, ставки по уровню, серия уборок, маршрут, история */
export function Points() {
  const { t, dt, locale } = useI18n();
  const trpc = useTRPC();
  const p = useQuery(trpc.client.points.summary.queryOptions());
  const history = useQuery(trpc.client.points.history.queryOptions());
  const [pay, setPay] = useState(false);
  const n = (v: number) => new Intl.NumberFormat(locale === 'zh' ? 'zh-CN' : locale).format(v);
  const date = (d: Date) => dt(d, { day: 'numeric', month: 'long' });
  const name = (m: Record<string, string> | null | undefined) => (m ? m[locale] || m.ru || m.en || '' : '');

  if (!p.data)
    return (
      <Center>
        <Spinner />
      </Center>
    );
  const d = p.data;
  const tier = d.tier as Level;
  return (
    <main className="app app--plain">
      <PageHead title={t('client.points.title')} />

      <Card tone="dark" className="bc-stack points-hero">
        <span className="bc-small" style={{ opacity: 0.75 }}>
          {t('client.points.balance')}
        </span>
        <div className="points-hero__num">{n(d.balance)}</div>
        <span className="bc-small" style={{ opacity: 0.85 }}>
          {t('client.points.equals', { amount: n(Math.max(0, d.balance)) })}
        </span>
        {d.pending > 0 && <span className="bc-small">⏳ {t('client.points.pending', { amount: n(d.pending), date: d.pendingNextAt ? date(d.pendingNextAt) : '—' })}</span>}
        {d.burnAt && <span className="bc-small">🔥 {t('client.points.burn', { amount: n(d.burnAmount), date: date(d.burnAt) })}</span>}
        <div className="bc-row" style={{ gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <Button size="sm" variant="light" disabled={d.balance < d.redeem.min} onClick={() => setPay(true)}>
            {t('client.points.pay')}
          </Button>
          <a className="bc-btn bc-btn--outline bc-btn--sm points-hero__ghost" href="#/profile?code=1">
            {t('client.points.enterCode')}
          </a>
        </div>
        {d.openRequests > 0 && <span className="bc-small">{t('client.points.requestPending')}</span>}
      </Card>

      <Card className="bc-stack">
        <div className="bc-between">
          <h2 className="bc-h3">{t('client.points.rates')}</h2>
          <Badge>{LEVEL_NAME[tier]}</Badge>
        </div>
        <table className="points-rates">
          <thead>
            <tr>
              <th />
              <th data-on={tier === 'BAREV' || undefined}>Barev</th>
              <th data-on={tier === 'BAREV_PLUS' || undefined}>Barev+</th>
              <th data-on={tier === 'ELITE' || undefined}>Elite</th>
            </tr>
          </thead>
          <tbody>
            {d.rates.map((r) => (
              <tr key={r.code}>
                <td>{name(r.name)}</td>
                <td data-on={tier === 'BAREV' || undefined}>{r.barev}%</td>
                <td data-on={tier === 'BAREV_PLUS' || undefined}>{r.plus}%</td>
                <td data-on={tier === 'ELITE' || undefined}>{r.elite}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        {tier === 'BAREV' && d.plusWouldGet > 0 && (
          <a href="#/membership" className="bc-small" style={{ color: 'var(--bc-accent)', fontWeight: 600 }}>
            {t('client.points.plusWouldGet', { amount: n(d.plusWouldGet) })}
          </a>
        )}
      </Card>

      <Card className="bc-stack">
        <h2 className="bc-h3">{t('client.points.streak')}</h2>
        <div className="points-dots" aria-hidden="true">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <span key={i} data-on={d.streak.count >= i || undefined} data-mark={i === 3 || i === 6 || undefined} />
          ))}
        </div>
        <p className="bc-small" style={{ margin: 0 }}>
          {d.streak.next
            ? t('client.points.streakLeft', { left: d.streak.next - d.streak.count, bonus: d.streak.next === 3 ? 2 : 3, days: d.streak.gapDays })
            : t('client.points.streakMax', { bonus: d.streak.bonusPct })}
        </p>
      </Card>

      {d.route && (
        <Card tone="home" className="bc-stack">
          <h2 className="bc-h3">{t('client.points.route')}</h2>
          <div className="points-dots" aria-hidden="true">
            {[1, 2, 3].map((i) => (
              <span key={i} data-on={d.route!.done >= i || undefined} />
            ))}
          </div>
          <p className="bc-small" style={{ margin: 0 }}>
            {t('client.points.routeText', { done: d.route.done, amount: n(d.route.amount), date: date(d.route.until) })}
          </p>
        </Card>
      )}

      <section className="bc-stack" style={{ gap: 8 }}>
        <h2 className="bc-h3">{t('client.points.history')}</h2>
        {!history.data ? (
          <Spinner />
        ) : history.data.length === 0 ? (
          <p className="bc-muted" style={{ margin: 0 }}>
            {t('client.points.empty')}
          </p>
        ) : (
          <Card tight className="bc-stack" style={{ gap: 0 }}>
            {history.data.map((r) => (
              <div key={`${r.type}:${r.id}`} className="points-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {r.type === 'bonus' ? name(r.bonus) : r.type === 'accrual' ? name(r.category) : t(`client.points.type.${r.type}`)}
                    {r.orderRef ? <span className="bc-muted"> {r.orderRef}</span> : null}
                  </div>
                  <div className="bc-small bc-muted">
                    {dt(r.at, { day: 'numeric', month: 'short' })} · {t(`client.points.status.${r.status}`)}
                    {r.status === 'PENDING' && r.availableAt ? ` · ${t('client.points.availableFrom', { date: date(r.availableAt) })}` : ''}
                    {r.expiresAt && r.status === 'ACTIVE' ? ` · ${t('client.points.until', { date: date(r.expiresAt) })}` : ''}
                  </div>
                  {r.comment && <div className="bc-small">💬 {r.comment}</div>}
                </div>
                <b className={r.amount > 0 ? 'points-plus' : 'points-minus'}>
                  {r.amount > 0 ? '+' : '−'}
                  {n(Math.abs(r.amount))}
                </b>
              </div>
            ))}
          </Card>
        )}
      </section>
      {pay && <PaySheet balance={d.balance} sharePct={d.redeem.sharePct} min={d.redeem.min} onClose={() => setPay(false)} />}
    </main>
  );
}

/** Оплатить бариками: выбрать заказ или ввести сумму — запрос уходит команде */
function PaySheet({ balance, sharePct, min, onClose }: { balance: number; sharePct: number; min: number; onClose: () => void }) {
  const { t, amd, dt } = useI18n();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const orders = useQuery(trpc.client.orders.list.queryOptions());
  const [orderId, setOrderId] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const order = orders.data?.active.find((o) => o.id === orderId);
  const max = order ? Math.min(balance, Math.floor(((order.totalPrice - order.pointsUsed) * sharePct) / 100)) : balance;
  const value = Number(amount) || 0;
  const send = useMutation(
    trpc.client.points.request.mutationOptions({
      onSuccess: () => {
        haptic.ok();
        toast(t('client.points.requested'));
        void qc.invalidateQueries();
        onClose();
      },
      onError: (e) => toast(errorText(t, e), 'error'),
    }),
  );
  const payable = orders.data?.active.filter((o) => o.paymentStatus === 'UNPAID') ?? [];
  return (
    <Sheet open onClose={onClose} label={t('client.points.pay')}>
      <div className="bc-stack">
        <div className="bc-between">
          <h2 className="bc-h2">{t('client.points.pay')}</h2>
          <button className="back" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="close" />
          </button>
        </div>
        <p className="bc-small" style={{ margin: 0 }}>
          {t('client.points.payHint', { pct: sharePct, min: amd(min).replace(/\s?֏$/, '') })}
        </p>
        <Field label={t('client.points.forOrder')}>
          <select className="bc-input" value={orderId} onChange={(e) => setOrderId(e.target.value)}>
            <option value="">{t('client.points.otherOrder')}</option>
            {payable.map((o) => (
              <option key={o.id} value={o.id}>
                #{o.number} · {t(`services.${o.serviceCode}.name`)} · {dt(o.scheduledAt, { day: 'numeric', month: 'short' })}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('client.points.amount')}>
          <input className="bc-input" inputMode="numeric" value={amount} placeholder={String(max)} onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 7))} />
        </Field>
        <p className="bc-small bc-muted" style={{ margin: '-6px 0 0' }}>
          {t('client.points.max', { max: amd(max).replace(/\s?֏$/, '') })}
        </p>
        {!order && (
          <Field label={t('client.points.note')}>
            <input className="bc-input" value={note} maxLength={300} placeholder={t('client.points.notePh')} onChange={(e) => setNote(e.target.value)} />
          </Field>
        )}
        <Button block disabled={send.isPending || value < min || value > max} onClick={() => send.mutate({ amount: value, orderId: orderId || undefined, note: note.trim() || undefined })}>
          {t('client.points.send')}
        </Button>
      </div>
    </Sheet>
  );
}
